import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, CheckCheck, Zap, DollarSign, Receipt, UserPlus, Scale, FileText, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { dateFmt } from "../lib/format";
import { Button } from "../components/ui/button";

const ICONS = {
  expense_added: DollarSign,
  expense_edited: Receipt,
  expense_deleted: Receipt,
  payment_submitted: DollarSign,
  payment_approved: Check,
  payment_rejected: DollarSign,
  member_added: UserPlus,
  member_removed: UserPlus,
  settlement_generated: FileText,
  settings_updated: Scale,
  admin_changed: UserPlus,
  spending_alert: AlertTriangle,
};

export default function Notifications() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data });
  const items = data?.notifications || [];
  const unread = data?.unread || 0;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-6 w-6 text-primary" /> Notifications
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{unread > 0 ? `${unread} unread` : "You're all caught up"}</p>
        </div>
        {unread > 0 && (
          <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read-btn" className="rounded-full">
            <CheckCheck className="h-4 w-4 mr-1.5" /> Mark all read
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="notifications-empty">
          <Zap className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No activity yet. Add an expense to get started.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="notification-center">
          {items.map((n) => {
            const Icon = ICONS[n.event] || Bell;
            const isAlert = n.event === "spending_alert";
            return (
              <div
                key={n.id}
                className={`flex items-start gap-3 rounded-2xl border p-3.5 ${isAlert ? "border-amber-400/50 bg-amber-50 dark:bg-amber-950/20" : n.read ? "border-border bg-card" : "border-primary/30 bg-primary/5"}`}
                data-testid={`notification-item`}
              >
                <div className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center ${isAlert ? "bg-amber-400/20 text-amber-600" : n.read ? "bg-secondary text-muted-foreground" : "bg-primary/15 text-primary"}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-snug">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{dateFmt(n.created_at)} · {new Date(n.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</p>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
